// MCP Security — shared axios fetch hook.
//
// Thin wrapper around axios that:
//   - returns { data, error, loading, refetch }
//   - optionally polls every `pollMs` (0 or omit to disable)
//   - cancels in-flight on unmount
//
// Used by the new MCP pages that mostly do GETs with no server state.
// Non-200 responses funnel into `error`; a 404 (e.g. endpoint not
// provisioned yet) yields { data: null, error: null } so the caller
// can decide the UI treatment itself.

import { useCallback, useEffect, useRef, useState } from "react";
import axios from "../../../../../../utils/axios/v1/userAxios";

export default function useMcpQuery(path, { pollMs = 0, skip = false } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(!skip);
  const cancelledRef = useRef(false);

  const load = useCallback(async () => {
    if (skip || !path) return;
    setLoading(true);
    try {
      const r = await axios.get(path);
      if (cancelledRef.current) return;
      setData(r.data);
      setError(null);
    } catch (e) {
      if (cancelledRef.current) return;
      // 404 is a "not provisioned yet" soft case the UI can show empty.
      if (e?.response?.status === 404) {
        setData(null);
        setError(null);
      } else {
        setError(e?.response?.data?.message || e.message || "failed");
      }
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, [path, skip]);

  useEffect(() => {
    cancelledRef.current = false;
    load();
    if (pollMs > 0 && !skip) {
      const t = setInterval(load, pollMs);
      return () => {
        cancelledRef.current = true;
        clearInterval(t);
      };
    }
    return () => {
      cancelledRef.current = true;
    };
  }, [load, pollMs, skip]);

  return { data, error, loading, refetch: load };
}
